<script setup>
import { computed, ref, watch } from "vue";
import { useTextStore } from "@/stores/useTextStore";
import { useUserStore } from "@/stores/useUserStore";
import UserCardModal from "@/components/User/UserCardModal.vue";
import TopicCardModal from "@/components/Topic/TopicCardModal.vue";
import AuthorCardModal from "@/components/Author/AuthorCardModal.vue";
import CategoryCardModal from "@/components/Category/CategoryCardModal.vue";
import PermissionView from "@/components/Generics/PermissionView.vue";
import EditTextForm from "@/components/Text/EditTextForm.vue";
import DeleteTextForm from "@/components/Text/DeleteTextForm.vue";

import { formatDateTimeForDisplay } from '@/utils/dateUtils'
import { toPlainText } from "@/utils/htmlProjection";
import { applyReadingAttrs } from "@/utils/htmlReadingAttrs";
import api from "@/services/api";
import { useDraggableModal } from "@/composables/useDraggableModal";

const props = defineProps({
  show: { type: Boolean, required: true },
  textId: { type: Number, required: true },
});

const emit = defineEmits(["close", "text-updated", "notify"]);

const textStore = useTextStore();
const userStore = useUserStore();

const isLoadingData = ref(true);

// 🔹 NOVO: arrastar o modal pelo cabeçalho. Nem todo uso desmonta o componente
// entre aberturas, então o watch de `show` recentraliza ao reabrir.
const {
  panelRef,
  panelStyle,
  isDragging,
  isDraggable,
  onHandlePointerDown,
  resetPosition,
} = useDraggableModal();

watch(
  () => props.show,
  (val) => {
    if (val) resetPosition();
  }
);

const showUserModal = ref(false);
const selectedUserId = ref(null);

// Topic
const showTopicCardModal = ref(false);
const selectedTopicIdForModal = ref(null);

// Autor
const showAuthorCardModal = ref(false);
const selectedAuthorIdForModal = ref(null);

// Gênero
const showCategoryCardModal = ref(false);
const selectedCategoryIdForModal = ref(null);

const showPermissionView = ref(false);

// Estado para controle do EditTextForm
const showEditModal = ref(false);

// Estado para controle do DeleteTextForm
const showDeleteModal = ref(false);

function openUserModal(userId) {
  if (!userId) return;
  selectedUserId.value = userId;
  showUserModal.value = true;
}

function closeUserModal() {
  showUserModal.value = false;
}

function openTopicCard(topicId) {
  if (!topicId) return;
  selectedTopicIdForModal.value = topicId;
  showTopicCardModal.value = true;
}

function closeTopicCard() {
  showTopicCardModal.value = false;
  selectedTopicIdForModal.value = null;
}

function openAuthorCard() {
  const t = text.value;
  if (!t || !t.authorId) return;
  selectedAuthorIdForModal.value = t.authorId;
  showAuthorCardModal.value = true;
}

function closeAuthorCard() {
  showAuthorCardModal.value = false;
  selectedAuthorIdForModal.value = null;
}

function openCategoryCard() {
  const t = text.value;
  if (!t) return;
  const id = t.categoryId ?? t.genreId ?? null;
  if (!id) return;
  selectedCategoryIdForModal.value = id;
  showCategoryCardModal.value = true;
}

function closeCategoryCard() {
  showCategoryCardModal.value = false;
  selectedCategoryIdForModal.value = null;
}

function openPermissionView() {
  showPermissionView.value = true;
}

function closePermissionView() {
  showPermissionView.value = false;
}

// Permissão de edição/exclusão
const canEdit = computed(() => {
  const logged = (userStore.currentUser?.userName ?? "").trim().toLowerCase();
  if (!logged) return false;
  if (logged === "sys") return true;
  return text.value?.createdByUserId === userStore.currentUser?.userId;
});

function openEditModal() {
  textStore.setIdText(props.textId);
  showEditModal.value = true;
}

function handleEditRefresh() {
  textStore.getTextById(props.textId);
  emit("text-updated");
}

function openDeleteModal() {
  showDeleteModal.value = true;
}

function handleDeleteClose() {
  showDeleteModal.value = false;
  emit("text-updated");
  emit("close");
}

function handleDeleteNotify(payload) {
  emit("notify", payload);
}

const canViewPermissions = computed(() => {
  const currentUserId = Number(userStore.currentUser?.userId);
  if (!currentUserId || !text.value) return false;
  return currentUserId === Number(text.value.createdByUserId);
});

const text = computed(() => {
  return (
    textStore.texts.find((t) => t.textId === props.textId) ||
    textStore.selectedText ||
    null
  );
});

watch(
  () => props.textId,
  async (newId) => {
    if (!newId) return;
    isLoadingData.value = true;
    try {
      await textStore.getTextById(newId);
    } catch (e) {
      console.error("Erro ao carregar texto no TextCardModal:", e);
    } finally {
      isLoadingData.value = false;
    }
  },
  { immediate: true }
);

const formattedCreatedAt = computed(() => {
  if (!text.value?.createdAt) return "";
  return formatDateTimeForDisplay(text.value.createdAt);
});

const formattedUpdatedAt = computed(() => {
  if (!text.value?.updatedAt) return "";
  return formatDateTimeForDisplay(text.value.updatedAt);
});

// Conteúdo REAL do corpo, sem a mensagem de ausência: é ele que decide se há o
// que renderizar como HTML. Com o fallback embutido, "Conteúdo do texto não
// disponível." entraria no `v-html` como se fosse o texto.
const rawTextBody = computed(() => {
  if (textStore.fileContent) return textStore.fileContent;
  if (text.value?.content) return text.value.content;
  if (text.value?.body) return text.value.body;
  return "";
});

const textBody = computed(
  () => rawTextBody.value || "Conteúdo do texto não disponível."
);

const isTextHtml = computed(() => text.value?.contentFormat === "html");

// O corpo sai do `fileContent`, que o watch de `textId` carrega no MESMO
// `getTextById` que trouxe o `contentFormat` — os dois nunca falam de textos
// diferentes.
const preparedBodyHtml = computed(() =>
  isTextHtml.value ? applyReadingAttrs(rawTextBody.value) : ""
);

function normalizeAvatarPath(path) {
  if (!path) return "";
  if (path.startsWith("http://") || path.startsWith("https://")) return path;
  const base = api.defaults.baseURL.replace(/\/api$/, "").replace(/\/+$/, "");
  const cleanPath = path.startsWith("/") ? path : `/${path}`;
  return `${base}${cleanPath}`;
}

function getCreatorAvatarUrl(textData) {
  if (!textData) return "";
  let path =
    textData.createdByUserPhotoPath || textData.createdByUser?.photoPath || "";
  if (!path) return "";
  return normalizeAvatarPath(path);
}

function getUpdaterAvatarUrl(textData) {
  if (!textData) return "";
  let path =
    textData.updatedByUserPhotoPath || textData.updatedByUser?.photoPath || "";
  if (!path) return "";
  return normalizeAvatarPath(path);
}

const titleText = computed(() => text.value?.title || "Sem título");
const subtitleText = computed(() => text.value?.subtitle || "");
const sourceText = computed(() => text.value?.source || "Não informada");
const authorText = computed(() => text.value?.authorName || "Não informado");

const genreText = computed(() => {
  return text.value?.genreName || text.value?.categoryName || "Não informado";
});

const topicsForDisplay = computed(() => {
  const result = [];
  const t = text.value;
  if (!t) return result;

  if (Array.isArray(t.topics) && t.topics.length > 0) {
    t.topics.forEach((item) => {
      const id = item.topicId ?? item.id ?? item.value ?? null;
      const name = item.topicName ?? item.name ?? item.label ?? null;
      if (name) result.push({ id, name });
    });
    return result;
  }

  if (
    Array.isArray(t.topicIds) &&
    t.topicIds.length > 0 &&
    Array.isArray(t.topicNames) &&
    t.topicNames.length === t.topicIds.length
  ) {
    t.topicIds.forEach((id, idx) => {
      const name = t.topicNames[idx];
      if (name) result.push({ id, name });
    });
    return result;
  }

  if (Array.isArray(t.topicIds) && t.topicIds.length > 0) {
    t.topicIds.forEach((id) => {
      const found = (textStore.topics || []).find(
        (x) => x.topicId === id || x.id === id || x.value === id
      );
      const name = found?.topicName || found?.name || found?.label || null;
      if (name) result.push({ id, name });
    });
    if (result.length > 0) return result;
  }

  if (Array.isArray(t.topicNames) && t.topicNames.length > 0) {
    t.topicNames.forEach((name) => {
      if (name) result.push({ id: null, name });
    });
    return result;
  }

  if (t.topicId != null || t.topicName) {
    const name =
      t.topicName ||
      (textStore.topics || []).find(
        (x) =>
          x.topicId === t.topicId || x.id === t.topicId || x.value === t.topicId
      )?.topicName;
    if (name) result.push({ id: t.topicId ?? null, name });
  }

  return result;
});

const textLengthValue = computed(() => {
  let len = 0;
  if (text.value?.textLength != null) {
    len = text.value.textLength;
  } else {
    // Sem `textLength` vindo da API, a conta é feita aqui — e sobre HTML o
    // `.length` da marcação contaria caractere de tag. `toPlainText` é a mesma
    // régua em que os offsets das notas são medidos, então o número exibido
    // aqui é o mesmo que o leitor enxerga.
    const content = rawTextBody.value;
    len = isTextHtml.value ? toPlainText(content).text.length : content.length;
  }
  return len.toLocaleString("pt-BR");
});

const activeStatusText = computed(() => {
  if (text.value?.active === undefined || text.value?.active === null)
    return "Não informado";
  return text.value.active ? "Ativo" : "Inativo";
});
</script>

<template>
  <div v-if="show" class="modal-overlay" @click.self="emit('close')">
    <div ref="panelRef" class="text-card-modal-content" :style="panelStyle">
      <div
        class="modal-header"
        :class="{
 'modal-header--draggable': isDraggable,
          'modal-header--dragging': isDragging,
        }"
        @pointerdown="onHandlePointerDown"
      >
        <h3 class="card-title">Detalhes do Texto</h3>
        <button
          @click="emit('close')"
          type="button"
          class="modal-close-btn"
          aria-label="Fechar"
        >
          <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path
              fill-rule="evenodd"
              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
              clip-rule="evenodd"
            />
          </svg>
        </button>
      </div>

      <div v-if="text && !isLoadingData" class="card-content-grid">
        <div class="text-details-col">
          <!-- Título + botões editar/apagar -->
          <div class="title-row">
            <h3 class="text-title-bold">
              {{ titleText }}
              <span class="title-id">(Id: {{ text.textId }})</span>
            </h3>

            <div class="title-actions">
              <button
                v-if="canEdit"
                type="button"
                class="btn-edit-text tooltip-bottom"
                data-tooltip="Editar texto"
                @click.stop="openEditModal"
              >
                <i class="fas fa-pencil-alt"></i>
              </button>

              <button
                v-if="canEdit"
                type="button"
                class="btn-delete-text tooltip-bottom"
                data-tooltip="Apagar texto"
                @click.stop="openDeleteModal"
              >
                <i class="fas fa-trash-alt"></i>
              </button>

              <button
                v-if="canViewPermissions"
                type="button"
                class="btn-view-permissions-text tooltip-bottom"
                data-tooltip="Ver permissões"
                @click.stop="openPermissionView"
              >
                <i class="fas fa-user-shield"></i>
              </button>
            </div>
          </div>

          <p v-if="subtitleText" class="text-subtitle-italic">
            {{ subtitleText }}
          </p>

          <div class="meta-grid meta-grid-spaced">
            <!-- AUTOR -->
            <div class="meta-item">
              <span class="meta-label">Autor</span>
              <div class="meta-value mt-1">
                <span v-if="!text.authorId" class="italic text-gray-400">
                  {{ authorText }}
                </span>
                <template v-else>
                  <span class="subcategory-chip" @click.stop="openAuthorCard">
                    {{ authorText }}
                  </span>
                </template>
              </div>
            </div>

            <!-- GÊNERO -->
            <div class="meta-item">
              <span class="meta-label">Gênero</span>
              <div class="meta-value mt-1">
                <span
                  v-if="!(text.categoryId || text.genreId)"
                  class="italic text-gray-400"
                >
                  {{ genreText }}
                </span>
                <template v-else>
                  <span class="subcategory-chip" @click.stop="openCategoryCard">
                    {{ genreText }}
                  </span>
                </template>
              </div>
            </div>

            <!-- Assuntos -->
            <div class="meta-item meta-item-full">
              <span class="meta-label">Assuntos</span>
              <div class="meta-value mt-1">
                <span
                  v-if="!topicsForDisplay.length"
                  class="italic text-gray-400"
                >
                  Nenhum assunto atribuído
                </span>

                <template v-else>
                  <i class="fas fa-lightbulb mr-2"></i>

                  <span
                    v-for="(topicItem, idx) in topicsForDisplay"
                    :key="idx"
                    class="subcategory-chip"
                    @click.stop="openTopicCard(topicItem.id)"
                  >
                    {{ topicItem.name }}
                  </span>
                </template>
              </div>
            </div>

            <!-- Qtde Caracteres + Status -->
            <div class="meta-item meta-item-full meta-item-row">
              <div class="meta-item-half">
                <span class="meta-label">Qtde Caracteres</span>
                <span class="meta-value">{{ textLengthValue }}</span>
              </div>

              <div class="meta-item-half">
                <span class="meta-label">Status</span>
                <span class="meta-value">
                  <span
                    :class="[
 'status-chip',
                      text?.active
                        ? 'status-chip--active'
                        : 'status-chip--inactive',
                    ]"
                  >
                    {{ activeStatusText }}
                  </span>
                </span>
              </div>
            </div>

            <div class="meta-item meta-item-full">
              <span class="meta-label">Fonte</span>
              <span class="meta-value meta-value-full">{{ sourceText }}</span>
            </div>
          </div>

          <hr class="content-separator" />

          <div class="footer-info">
            <div class="users-row">
              <!-- Criador -->
              <div
                class="user-chip user-chip-clickable"
                @click="openUserModal(text.createdByUserId)"
              >
                <div class="chip-avatar">
                  <div
                    v-if="getCreatorAvatarUrl(text)"
                    class="chip-avatar-img"
                    :style="{
                      backgroundImage: `url('${getCreatorAvatarUrl(text)}')`,
                    }"
                  ></div>
                  <i
                    v-else
                    class="fas fa-user chip-avatar-icon"
                    aria-hidden="true"
                  ></i>
                </div>
                <div class="chip-text">
                  <span class="chip-label">Criador</span>
                  <span class="chip-name">
                    {{ text.createdByUserName || "Não informado" }}
                  </span>
                  <span v-if="formattedCreatedAt" class="chip-date">
                    {{ formattedCreatedAt }}
                  </span>
                </div>
              </div>

              <!-- Atualizador -->
              <div
                class="user-chip user-chip-clickable"
                @click="openUserModal(text.updatedByUserId)"
              >
                <div class="chip-avatar">
                  <div
                    v-if="getUpdaterAvatarUrl(text)"
                    class="chip-avatar-img"
                    :style="{
                      backgroundImage: `url('${getUpdaterAvatarUrl(text)}')`,
                    }"
                  ></div>
                  <i
                    v-else
                    class="fas fa-user chip-avatar-icon"
                    aria-hidden="true"
                  ></i>
                </div>
                <div class="chip-text">
                  <span class="chip-label">Atualizador</span>
                  <span class="chip-name">
                    {{ text.updatedByUserName || "Não informado" }}
                  </span>
                  <span v-if="formattedUpdatedAt" class="chip-date">
                    {{ formattedUpdatedAt }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="selected-text-col">
          <label class="form-label text-label">Corpo do texto</label>
          <blockquote
            v-if="isTextHtml && preparedBodyHtml"
            class="themed-blockquote themed-blockquote--html p-3 border-l-4 rounded break-words"
          >
            <!-- Seguro: o corpo chega já normalizado e filtrado pela whitelist
                 do HtmlNormalizer (backend) antes de ser gravado. O card não
                 ancora nota nenhuma neste HTML, então não precisa do
                 renderizador recursivo do leitor — só do `applyReadingAttrs`,
                 que repõe o que o atalho perderia (link externo em aba nova,
                 imagem sem vazar o Referer). -->
            <div class="text-body--html" v-html="preparedBodyHtml"></div>
          </blockquote>
          <blockquote
            v-else
            class="themed-blockquote p-3 border-l-4 rounded whitespace-pre-wrap break-words font-mono"
          >
            {{ textBody }}
          </blockquote>
        </div>
      </div>

      <div v-else class="card-body loading-state">
        <i class="fas fa-spinner fa-spin"></i> Carregando detalhes do texto...
      </div>

    </div>
  </div>

  <!-- Sub-modais via Teleport.
       Todos são position: fixed e o painel deste card ganha transform ao ser
       arrastado — um ancestral com transform viraria o containing block deles,
       fazendo o overlay cobrir só o painel e acompanhar o arrasto. Fora do
       painel no DOM, seguem ancorados na viewport. -->
  <Teleport to="body">
    <UserCardModal
      v-if="showUserModal"
      :show="showUserModal"
      :userId="selectedUserId"
      @close="closeUserModal"
    />

    <TopicCardModal
      v-if="showTopicCardModal"
      :show="showTopicCardModal"
      :topicId="selectedTopicIdForModal"
      @close="closeTopicCard"
    />

    <AuthorCardModal
      v-if="showAuthorCardModal"
      :show="showAuthorCardModal"
      :authorId="selectedAuthorIdForModal"
      @close="closeAuthorCard"
    />

    <CategoryCardModal
      v-if="showCategoryCardModal"
      :show="showCategoryCardModal"
      :categoryId="selectedCategoryIdForModal"
      @close="closeCategoryCard"
    />

    <PermissionView
      v-if="showPermissionView"
      :show="showPermissionView"
      entityType="text"
      :textId="props.textId"
      title="Permissões do texto"
      @close="closePermissionView"
    />

    <EditTextForm
      v-if="showEditModal"
      :textId="props.textId"
      @close="showEditModal = false"
      @text-updated="handleEditRefresh"
    />
    <DeleteTextForm
      v-if="showDeleteModal && text"
      :text="text"
      @close="handleDeleteClose"
      @text-deleted="handleDeleteClose"
      @notify="handleDeleteNotify"
    />
  </Teleport>
</template>

<style scoped>
.modal-overlay {
  width: 100%;
  height: 100%;
}

.text-card-modal-content {
  background: var(--surface);
  border-radius: 12px;
  width: 90%;
  max-width: 1000px;
  position: relative;
  box-shadow: var(--shadow-modal);

  margin-bottom: 0;
  overflow: hidden;
  max-height: calc(100vh - 40px);

  display: flex;
  flex-direction: column;
}

.card-title {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 600;
  color: var(--text-dark);
}

.card-content-grid {
  display: flex;
  padding: 1rem;
  gap: 1.25rem;

  flex: 1;
  min-height: 0;
  overflow-y: auto;
}

.text-details-col {
  flex: 1;
  display: flex;
  flex-direction: column;
  padding-right: 1rem;
  border-right: 1px solid var(--border-color);
}

.selected-text-col {
  flex: 1;
  display: flex;
  flex-direction: column;
}

/* Linha do título com botões */
.title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  margin-bottom: 12px;
}

.title-actions {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}

.text-title-bold {
  font-size: 1.05rem;
  font-weight: 700;
  margin: 0;
  color: var(--text-dark);
  line-height: 1.25;
}

.title-id {
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--text-muted);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-left: 4px;
}

.text-subtitle-italic {
  font-style: italic;
  color: var(--text-light);
  margin: 0 0 12px 0;
}

/* ── Botão Editar ── */
.btn-edit-text {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  padding: 0;
  border-radius: 5px;
  border: 1px solid var(--info-border);
  background-color: var(--info-bg);
  color: var(--info-text);
  cursor: pointer;
  transition: all 0.15s ease;
  position: relative;
}
.btn-edit-text i {
  font-size: 0.6rem;
}
.btn-edit-text:hover {
  background-color: var(--info-border);
  transform: translateY(-1px);
}

/* ── Botão Apagar ── */
.btn-delete-text {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  padding: 0;
  border-radius: 5px;
  border: 1px solid var(--danger-bg);
  background-color: var(--danger-bg);
  color: var(--danger-text);
  cursor: pointer;
  transition: all 0.15s ease;
  position: relative;
}
.btn-delete-text i {
  font-size: 0.6rem;
}
.btn-delete-text:hover {
  background-color: var(--danger-bg);
  transform: translateY(-1px);
}

/* ── Botão Ver permissões ── */
.btn-view-permissions-text {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  padding: 0;
  border-radius: 5px;
  border: 1px solid var(--border-color);
  background-color: var(--neutral-100);
  color: var(--text-dark);
  cursor: pointer;
  transition: all 0.15s ease;
  position: relative;
}
.btn-view-permissions-text i {
  font-size: 0.6rem;
}
.btn-view-permissions-text:hover {
  background-color: var(--border-color);
  color: var(--text-dark);
  transform: translateY(-1px);
}

/* Tooltip */
.tooltip-bottom {
  position: relative;
}
.tooltip-bottom::after {
  content: attr(data-tooltip);
  position: absolute;
  top: calc(100% + 6px);
  left: 50%;
  transform: translateX(-50%) translateY(-2px);
  background: var(--tooltip-bg);
  color: var(--tooltip-text);
  font-size: 0.68rem;
  font-weight: 500;
  line-height: 1;
  padding: 5px 8px;
  border-radius: 4px;
  white-space: nowrap;
  opacity: 0;
  transition: opacity 0.15s ease, transform 0.15s ease;
  pointer-events: none;
  z-index: 80;
}
.tooltip-bottom:hover::after {
  opacity: 1;
  transform: translateX(-50%) translateY(0);
  transition-delay: 0.8s;
}

.meta-grid {
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
}

.meta-grid-spaced {
  gap: 12px 14px;
}

.footer-info {
  padding-top: 8px;
  width: 100%;
}

.users-row {
  display: flex;
  gap: 12px;
  flex-direction: row;
  flex-wrap: wrap;
  margin-top: 8px;
}

.text-label {
  font-weight: 500;
  font-size: 0.9rem;
  color: var(--text-dark);
  margin-bottom: 0.3rem;
  display: block;
}

.subcategory-chip {
  background-color: #e0f2fe;
  color: #0369a1;
  padding: 2px 8px;
  border-radius: 6px;
  font-weight: 500;
  font-size: 0.75rem;
  margin-left: 0;
  margin-right: 6px;
  cursor: pointer;
}

.subcategory-chip:hover {
  background-color: #bfdbfe;
}

.meta-item-row {
  display: flex;
  flex-direction: row;
  gap: 12px;
}

.meta-item-half {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.themed-blockquote {
  background-color: var(--bg-header);
  border-left: 4px solid var(--brand-light);
  color: var(--text-dark);
  padding: 1rem;
  border-radius: 8px;
  line-height: 1.35;
  height: 640px;
  max-height: 640px;
  overflow-y: auto;
  box-sizing: border-box;
  font-size: 0.9rem;
}

</style>